import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { WorkflowStateProvider } from "./components/workflow/WorkflowStateContext";

/* ---------------- USER SCREENS ---------------- */
import StartRegisterScreen from "./screens/StartRegister";
import RegisterScreen from "./screens/Register";
import ChoosePlan from "./screens/ChoosePlan";
import BillingPage from "./screens/BillingPage";
import LoginScreen from "./screens/Login";
import HomeScreen from "./screens/Home";
import MerchantHome from "./screens/MerchantHome";
import SalesAnalytics from "./screens/SalesAnalytics";
import MapSalesScreen from "./screens/MapSales";
import ImportDataScreen from "./screens/ImportData";
import ImportDataEditScreen from "./screens/ImportDataEdit";
import PromoScreenAds from "./screens/PromoScreenAds";
import Settings from "./screens/Settings";
import Scanner from "./screens/Scanner";
import PrivacyPolicy from "./screens/PrivacyPolicy";
import CopyrightPolicy from "./screens/CopyrightPolicy";
import MasterServiceAgreement from "./screens/MasterServiceAgreement";
import { MSA_PATH } from "./constants/msa";
import Support from "./screens/Support";
import DeviceLogin from "./screens/DeviceLogin";
import Mode from "./screens/Mode";
import AIMenuScreen from "./screens/AIMenuScreen";
import MenuPreviewPage from "./screens/MenuPreviewPage";
import DataExtractionPage from "./screens/DataExtractionPage";
import PromoLogin from "./screens/PromoLogin";
import CreateSchedulePage from "./screens/CreateSchedulePage";
import OnlineOrderPage from "./screens/OnlineOrderPage";
import OnlineOrderSettingsPage from "./screens/OnlineOrderSettingsPage";
import ScheduleHubPage from "./screens/ScheduleHubPage";
import StoreListPage from "./screens/StoreListPage";
import CreateStorePage from "./screens/CreateStorePage";
import OrganisationPage from "./screens/OrganisationPage";
import TeamMembers from "./screens/TeamMembers";
import AddTeamMember from "./screens/AddTeamMember";

/* ---------------- ADMIN SCREENS ---------------- */
import AdminLogin from "./screens/AdminLogin";
import AdminMode from "./screens/AdminMode";
import AdminAIMenu from "./screens/AdminAIMenu";
import AdminUsage from "./screens/AdminUsage";
import AdminWorkflow from "./screens/AdminWorkflow";
import AdminPromoScreenAds from "./screens/PromoAds";
import AdminLayout from "./components/Layout";
import Dashboard from "./screens/Dashboard";
import ManageAdmin from "./screens/AddonAdmin";
import LoginAsMerchant from "./screens/LoginAsMerchant";
import MobileAppLayout from "./components/MobileAppLayout";

/* ---------------- FIREBASE ---------------- */
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./config/config-firebase";

const App = () => {
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(!!localStorage.getItem("customer_id"));
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(!!localStorage.getItem("admin_token"));
  const [authChecked, setAuthChecked] = useState(false);

  /* ---------- CHECK AUTH ON LOAD ---------- */
  useEffect(() => {
    // 🔹 USER (Firebase or Promo Login)
    const unsub = onAuthStateChanged(auth, (user) => {
      const hasPromoSession = !!localStorage.getItem("customer_id");
      setIsUserLoggedIn(Boolean(user) || hasPromoSession);
      setAuthChecked(true); // ✅ Set to true only after the first auth check
    });

    // 🔹 ADMIN (API token)
    const adminToken = localStorage.getItem("admin_token");
    setIsAdminLoggedIn(Boolean(adminToken));

    // ✅ Safety timeout: If Firebase is slow, proceed with localStorage state
    const timer = setTimeout(() => {
      setAuthChecked(true);
    }, 2000);

    return () => {
      unsub();
      clearTimeout(timer);
    };
  }, []);

  if (!authChecked) return null;

  // Helper component for protected routes
  const ProtectedRoute = ({ children }) => {
    const hasSession = !!localStorage.getItem("customer_id") || !!localStorage.getItem("admin_token");
    return isUserLoggedIn || hasSession ? children : <Navigate to="/login" />;
  };

  const MobileShell = ({ children, requireAuth = true }) => {
    const content = <MobileAppLayout>{children}</MobileAppLayout>;
    return requireAuth ? <ProtectedRoute>{content}</ProtectedRoute> : content;
  };

  return (
    <WorkflowStateProvider>
    <Routes>
      {/* ---------------- USER ROUTES ---------------- */}
      <Route path="/" element={<HomeScreen />} />
      <Route
        path="/home"
        element={
          <MobileShell>
            <MerchantHome />
          </MobileShell>
        }
      />
      <Route
        path="/sales-analytics"
        element={
          <MobileShell>
            <SalesAnalytics />
          </MobileShell>
        }
      />
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/order/:customerId" element={<OnlineOrderPage />} />
      <Route path="/start-register" element={<StartRegisterScreen />} />
      <Route path="/register" element={<RegisterScreen />} />
      <Route
        path="/choose-plan"
        element={
          <ProtectedRoute>
            <ChoosePlan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/map-sales"
        element={
          <MobileShell>
            <MapSalesScreen />
          </MobileShell>
        }
      />

      <Route
        path="/import-data"
        element={
          <MobileShell>
            <ImportDataScreen />
          </MobileShell>
        }
      />
      <Route
        path="/edit-data"
        element={
          <MobileShell>
            <ImportDataEditScreen />
          </MobileShell>
        }
      />
      <Route
        path="/ai-menu"
        element={
          <MobileShell>
            <AIMenuScreen />
          </MobileShell>
        }
      />
      <Route
        path="/menu-preview"
        element={
          <MobileShell>
            <MenuPreviewPage />
          </MobileShell>
        }
      />
      <Route
        path="/extract-data"
        element={
          <MobileShell>
            <DataExtractionPage />
          </MobileShell>
        }
      />
      <Route
        path="/user-settings"
        element={
          <MobileShell>
            <Settings />
          </MobileShell>
        }
      />
      <Route
        path="/online-ordering"
        element={
          <MobileShell>
            <OnlineOrderSettingsPage />
          </MobileShell>
        }
      />
      <Route
        path="/organisation"
        element={
          <ProtectedRoute>
            <OrganisationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-scan"
        element={
          <MobileShell>
            <Scanner />
          </MobileShell>
        }
      />
      <Route
        path="/stores"
        element={
          <ProtectedRoute>
            <StoreListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-members"
        element={
          <ProtectedRoute>
            <TeamMembers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/add-team-member"
        element={
          <ProtectedRoute>
            <AddTeamMember />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-store"
        element={
          <ProtectedRoute>
            <CreateStorePage />
          </ProtectedRoute>
        }
      />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/copyright-policy" element={<CopyrightPolicy />} />
      {/* Unversioned URL always forwards to the current version — old
          bookmarks/links keep working, but the address bar (and anything
          shared going forward) shows exactly which version is in effect. */}
      <Route
        path="/master-service-agreement"
        element={<Navigate to={MSA_PATH} replace />}
      />
      {/* React Router v6 only treats a segment as dynamic when the colon
          starts the segment — "v:version" is matched as literal text, not a
          param, so the version must live in its own segment here. */}
      <Route
        path="/master-service-agreement/:version"
        element={<MasterServiceAgreement />}
      />
      <Route path="/support" element={<Support />} />
      <Route path="/device-login" element={<DeviceLogin />} />
      <Route
        path="/modes"
        element={
          <MobileShell requireAuth={false}>
            <Mode />
          </MobileShell>
        }
      />
      <Route
        path="/promo-ads/:modeId"
        element={
          <MobileShell requireAuth={false}>
            <PromoScreenAds />
          </MobileShell>
        }
      />
      <Route
        path="/schedule-hub"
        element={
          <ProtectedRoute>
            <ScheduleHubPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={<Navigate to="/schedule-hub" replace />}
      />
      <Route
        path="/create-schedule"
        element={
          <ProtectedRoute>
            <CreateSchedulePage />
          </ProtectedRoute>
        }
      />
      <Route path="/promo-login" element={<PromoLogin />} />

      {/* ---------------- ADMIN LOGIN ---------------- */}
      <Route
        path="/admin/login"
        element={
          isAdminLoggedIn ? (
            <Navigate to="/admin/mode" replace />
          ) : (
            <AdminLogin />
          )
        }
      />

      {/* ---------------- ADMIN PROTECTED ---------------- */}
      <Route
        path="/admin"
        element={
          isAdminLoggedIn ? (
            <AdminLayout />
          ) : (
            <Navigate to="/admin/login" replace />
          )
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="mode" element={<AdminMode />} />
        <Route path="ai-menu" element={<AdminAIMenu />} />
        <Route path="login-as-merchant" element={<LoginAsMerchant />} />
        <Route path="manage-admin" element={<ManageAdmin />} />
        <Route path="workflow" element={<AdminWorkflow />} />
        <Route path="usage" element={<AdminUsage />} />
        <Route path="promo-ads/:modeId" element={<AdminPromoScreenAds />} />
      </Route>

      {/* ---------------- FALLBACK ---------------- */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </WorkflowStateProvider>
  );
};

export default App;
